import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Form, FormControl, FormGroup, FormLabel, Alert, Spinner, Modal } from "react-bootstrap";
import ProductImg from "../../../images/product-img.png"
import Profile from "../../../images/profile-icon.svg"
import Call from "../../../images/call-icon.svg"
import Email from "../../../images/email-icon.png"
import LocationIcon from "../../../images/location-icon.svg"
import IconHome from "../../../images/icon-home.png"
import PhoneIcon from "../../../images/phone-icon.svg";
import AddressIcon from "../../../images/address-icon.svg";
import MailIcon from "../../../images/email-icon.png";
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

// Import milestone-specific icons (same icons as Orders)
import IconCupCake from "../../../images/orders/icon_cup-cake.png"
import IconGiftType from "../../../images/orders/icon_gift-type.png"
import IconCalendarFixed from "../../../images/orders/icon_calendar-fixed.png"
import IconCalendarOutlook from "../../../images/orders/icon_calendar-outlook.png"
import IconComments from "../../../images/orders/icon_comments.png"
import IconEnvelope from "../../../images/orders/icon_envelope.png"
import IconCallOrders from "../../../images/orders/icon_call.png"
import IconLocation from "../../../images/orders/icon_location.png"
import IconTransaction from "../../../images/orders/icon_trasaction.png"
import IconCard from "../../../images/orders/icon_card.png"
import IconCurrency from "../../../images/orders/icon_curr_icon.png"
import IconDeliveryVan from "../../../images/orders/icon_delivery-van.png"
import { Button } from 'react-bootstrap';
import { toast } from "react-toastify";
import { FaArrowLeftLong, FaDownload } from "react-icons/fa6";
import Swal from 'sweetalert2';
import { ordersApi } from '../Orders/OrdersApi.tsx';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import "./Orders.scss"

interface MilestoneGiftDetails {
  _id: string;
  orderId: string;
  orderDate: string;
  userName: string;
  occasion: string;
  type: string;
  title?: string;
  milestone?: string;
  message?: string;
  occasionType?: string;
  date: string;
  milestoneEstimateDate?: string;
  milestoneConfirmed?: boolean;
  giftStatus: string;
  paymentStatus: string;
  additionalComments: string;
  recipientSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    alternatePhoneNumber?: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
  };
  userSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
  };
  userDetails?: {
    customerId: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    };
    profileUrl?: string;
    accountStatus?: string;
  };
  giftSection?: {
    type: string;
    monetary?: { title: string; amount: number };
    giftCard?: { title: string; selectedAmount: number; imageUrl: string; sendMethod: string };
    physicalItem?: { title: string; price: number; description: string; imageUrl: string };
  };
  messageType?: string;
  deliverySchedule: string;
  recurringDetails?: {
    frequency: string;
    numberOfYears: number;
  };
  paymentDetails?: {
    transactionId: string;
    paymentMethod?: string;
    amount?: number;
    amountPaid: number;
    cardBrand: string;
    cardLast4: string;
    cardExpMonth: number;
    cardExpYear: number;
    paymentDate?: string;
  };
  messages?: Array<{
    title: string;
    content: string;
  }>;
}


const MilestoneDetails = () => {
  const params = useParams();
  const { id: milestoneId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { withLoader } = useApiLoader();

  // Also try to extract ID directly from URL as fallback
  const urlId = window.location.pathname.split('/milestone-details/')[1];
  const finalId = milestoneId || urlId;


  const [milestoneDetails, setMilestoneDetails] = useState<MilestoneGiftDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [milestoneConfirmation, setMilestoneConfirmation] = useState<string>('');
  const [initialLoadDone, setInitialLoadDone] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  // Handle audio download
  const handleDownloadAudio = async () => {
    if (!milestoneDetails) return;
    try {
      setDownloading(true);
      await ordersApi.downloadAudio(milestoneDetails._id);
      toast.success('Audio downloaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download audio');
    } finally {
      setDownloading(false);
    }
  };


  useEffect(() => {
    const fetchMilestoneData = async () => {
      console.log('fetchMilestoneData called with finalId:', finalId);
      console.log('initialLoadDone:', initialLoadDone);

      if (initialLoadDone) {
        console.log('Early return - already loaded');
        return;
      }

      if (!finalId) {
        console.log('No finalId provided, using mock data');
        // Fallback to mock data if no ID
        const mockData = {
          _id: "mock-id",
          orderId: "GFH825805",
          orderDate: "2025-04-25T00:00:00.000Z",
          userName: "Mock User",
          occasion: "Mock Birthday",
          type: "Milestone",
          milestoneConfirmed: false,
          giftStatus: "Upcoming",
          paymentStatus: "Paid"
        };
        setMilestoneDetails(mockData as any);
        setMilestoneConfirmation('No');
        setLoading(false);
        setInitialLoadDone(true);
        return;
      }

      try {
        console.log('Setting loading to true');
        setLoading(true);
        console.log('Calling getSingleOrder with ID:', finalId);
        // Use the loader for API call
        const response = await withLoader(
          () => ordersApi.getSingleOrder(finalId),
          'Loading milestone gift details...'
        );
        console.log('API response:', response);

        if (response.status === 'Success' && response.data) {
          console.log('Setting milestone details:', response.data);
          setMilestoneDetails(response.data);
          setMilestoneConfirmation(response.data.milestoneConfirmed ? 'Yes' : 'No');
        } else {
          console.log('API error, using fallback data:', response.message);
          // Fallback to mock data if API fails
          const mockData = {
            _id: finalId,
            orderId: "GFH825805",
            orderDate: "2025-04-25T00:00:00.000Z",
            userName: "API Failed User",
            occasion: "Mock Birthday",
            type: "Milestone",
            milestoneConfirmed: false,
            giftStatus: "Upcoming",
            paymentStatus: "Paid"
          };
          setMilestoneDetails(mockData as any);
          setMilestoneConfirmation('No');
          setError(`API Error: ${response.message || 'Failed to fetch milestone details'}`);
        }
      } catch (error: any) {
        console.error('Catch block - Error fetching milestone details:', error);
        // Fallback to mock data on error
        const mockData = {
          _id: finalId,
          orderId: "GFH825805",
          orderDate: "2025-04-25T00:00:00.000Z",
          userName: "Error Fallback User",
          occasion: "Mock Birthday",
          type: "Milestone",
          milestoneConfirmed: false,
          giftStatus: "Upcoming",
          paymentStatus: "Paid"
        };
        setMilestoneDetails(mockData as any);
        setMilestoneConfirmation('No');
        setError(error.message || 'Failed to fetch milestone details');
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
        setInitialLoadDone(true);
      }
    };

    fetchMilestoneData();
  }, [finalId, initialLoadDone]);

  // Handle milestone confirmation update with confirmation
  const handleMilestoneConfirmationUpdate = async () => {
    if (!milestoneDetails) return;

    const confirmationText = milestoneConfirmation === 'Yes'
      ? 'confirm the milestone verification'
      : 'mark milestone as not confirmed';

    const result = await Swal.fire({
      title: 'Update Milestone Confirmation?',
      text: `Are you sure you want to ${confirmationText}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3957A0',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Update Confirmation',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);

      // Convert the string value to boolean
      const confirmationBoolean = milestoneConfirmation === 'Yes';

      // Make actual API call
      const response = await ordersApi.updateGiftVerification(milestoneDetails._id, {
        milestoneConfirmed: confirmationBoolean
      });

      if (response.status === 'Success') {
        toast.success('Milestone confirmation updated successfully');

        Swal.fire({
          title: 'Success!',
          text: `Milestone confirmation has been ${milestoneConfirmation === 'Yes' ? 'confirmed' : 'updated'}`,
          icon: 'success',
          confirmButtonColor: '#3957A0'
        }).then(() => {
          // Redirect to milestone gifts page to see updated data
          navigate('/milestonegifts');
        });
      } else {
        throw new Error(response.message || 'Failed to update milestone confirmation');
      }
    } catch (error: any) {
      console.error('Error updating milestone confirmation:', error);
      toast.error(error.message || 'Failed to update milestone confirmation');

      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to update milestone confirmation',
        icon: 'error',
        confirmButtonColor: '#3957A0'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format date and time for display
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }) + ' - ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get initials from name (similar to Header component)
  const getInitials = (name: string) => {
    if (!name) return 'U'; // Default to 'U' for User if no name
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getStatusClass = (status: string | undefined | null): string => {
    return typeof status === "string" && status.toLowerCase() === "active"
      ? "tag active"
      : "tag inactive";
  };

  // Capitalize text helper
  const capitalizeText = (text: string) => {
    if (!text) return 'N/A';
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get gift display details
  const getGiftDetails = () => {
    if (!milestoneDetails) return null;

    if (milestoneDetails.giftSection) {
      const section: any = milestoneDetails.giftSection;
      
      // Handle different gift types with proper image and amount logic
      switch (section.type) {
        case 'GiftCard':
          return {
            image: section.giftCard?.imageUrl || ProductImg,
            title: section.giftCard?.title || milestoneDetails.title || 'Gift Card',
            description: `${section.giftCard?.title || 'Gift Card'} - ${section.giftCard?.sendMethod || 'Digital'} delivery`,
            amount: section.giftCard?.selectedAmount || 0
          };
        
        case 'PhysicalItem':
          return {
            image: section.physicalItem?.imageUrl || ProductImg,
            title: section.physicalItem?.title || milestoneDetails.title || 'Physical Item',
            description: section.physicalItem?.description || 'Physical gift item',
            amount: section.physicalItem?.price || 0
          };
        
        case 'Monetary':
          return {
            image: ProductImg,
            title: section.monetary?.title || milestoneDetails.title || 'Monetary Gift',
            description: 'Monetary gift',
            amount: section.monetary?.amount || 0
          };
        
        default:
          return {
            image: ProductImg,
            title: milestoneDetails.title || 'Gift Item',
            description: `${section.type} gift for milestone`,
            amount: 0
          };
      }
    }

    return {
      image: ProductImg,
      title: milestoneDetails.title || 'Milestone Gift',
      description: 'Milestone gift',
      amount: 0
    };
  };

  // Loading state
  if (loading) {
    return (
      <section className="main-body">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading milestone gift details...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="main-body">
        <Alert variant="danger" className="m-4">
          <h5>Error Loading Milestone Gift</h5>
          <p>{error}</p>
          <Button color="primary" onClick={() => navigate('/milestonegifts')}>
            Back to Milestone Gifts
          </Button>
        </Alert>
      </section>
    );
  }

  // No data state
  if (!milestoneDetails) {
    return (
      <section className="main-body">
        <Alert variant="warning" className="m-4">
          <h5>Milestone Gift Not Found</h5>
          <p>The requested milestone gift could not be found.</p>
          <Button color="primary" onClick={() => navigate('/milestonegifts')}>
            Back to Milestone Gifts
          </Button>
        </Alert>
      </section>
    );
  }

  const giftDetails = getGiftDetails();

  return (
    <>
      <section className="main-body">
        {/* Header Section */}
        <section className="order-header-section">
          <div className="container-fluid">
            <div className="row align-items-center mb-4">
              <div className="col-md-8">
                {/* <h1 className="order-title">Order ID: {milestoneDetails.orderId}</h1> */}
                <nav className="breadcrumb-nav">
                  <Link to="/milestonegifts" className="text-decoration-none " style={{ color: '#3957A0' }}>
                    <span><img src={IconHome} alt="home" /> <span className='my-auto'>Milestone Gifts</span></span>
                  </Link>
                  <span className="mx-2 text-muted">›</span>
                  <span className="text-dark">{milestoneDetails.orderId}</span>
                </nav>
              </div>
              <div className="col-md-4 text-end">
                <Button color="secondary" className='bg-transparent border-0 fw-bold' style={{ color: '#3957A0' }} onClick={() => navigate('/milestonegifts')}>
                  <FaArrowLeftLong color='#3957A0' /> Back
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Profile Header */}
        {milestoneDetails.userDetails && (
          <div className='profile-area'>
            <div className='row justify-content-between' style={{ alignItems: "start" }}>
              <aside className='profile-block col-xl-3 col-lg-3'>
                <figure>
                  {milestoneDetails.userDetails.profileUrl ? (
                    <img
                      src={milestoneDetails.userDetails.profileUrl}
                      alt={milestoneDetails.userDetails.fullName || "Profile"}
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = 'profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center';
                          fallbackDiv.style.width = '100px';
                          fallbackDiv.style.height = '100px';
                          fallbackDiv.textContent = getInitials(milestoneDetails.userDetails?.fullName || '');
                          parent.appendChild(fallbackDiv);
                        }
                      }}
                    />
                  ) : (
                    <div className="profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px' }}>
                      {getInitials(milestoneDetails.userDetails.fullName)}
                    </div>
                  )}
                </figure>
                <div className="profile-cont">
                  <h3>{capitalizeText(milestoneDetails.userDetails.fullName) || "No name provided"}</h3>
                  <span className={getStatusClass(milestoneDetails.userDetails.accountStatus)}>
                    {capitalizeText(milestoneDetails.userDetails.accountStatus) || "Unknown"}
                  </span>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={MailIcon} alt='Email' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Email</h3>
                    <p>{milestoneDetails.userDetails?.email || milestoneDetails.userSnapshot?.email || "Not provided"}</p>
                  </div>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={AddressIcon} alt='Phone' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Phone</h3>
                    <p>{formatPhoneNumber(milestoneDetails.userDetails.phoneNumber) || "Not provided"}</p>
                  </div>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={AddressIcon} alt='Address' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Address</h3>
                    <p>
                      {milestoneDetails.userDetails.address?.address1 || milestoneDetails.userDetails.address?.city || milestoneDetails.userDetails.address?.state || milestoneDetails.userDetails.address?.zip
                        ? [
                            milestoneDetails.userDetails.address?.address1,
                            milestoneDetails.userDetails.address?.city,
                            milestoneDetails.userDetails.address?.state,
                            milestoneDetails.userDetails.address?.zip
                          ].filter(Boolean).join(', ')
                        : 'Not provided'
                      }
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* Combined Details Block */}
        <div className="details-block">
          <div className="details-block-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="section-title mb-0 fw-bold" style={{ fontSize: '20px' }}>Gift Information</h2>
              <div className="d-flex gap-2 align-items-center">
                <span className={`badge ${milestoneDetails.giftStatus === 'Delivered' ? 'bg-success' :
                    milestoneDetails.giftStatus === 'Shipped' ? 'bg-info' :
                      milestoneDetails.giftStatus === 'Processing' ? 'bg-warning text-dark' :
                        'bg-secondary'
                  }`}>
                  {milestoneDetails.giftStatus || 'Pending'}
                </span>
                {milestoneDetails.messageType === 'Physical' && (
                  <Button
                    size="sm"
                    disabled={downloading}
                    onClick={handleDownloadAudio}
                    style={{
                      backgroundColor: '#3957A0',
                      borderColor: '#3957A0',
                      borderRadius: '8px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <FaDownload size={14} />
                    {downloading ? 'Downloading...' : 'Download Audio'}
                  </Button>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCupCake} alt="Gift Title" width="32" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Gift Title</label>
                      <p className="fw-bold mb-0">{giftDetails?.title || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconGiftType} alt="Gift Type" width="32" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Gift Type</label>
                      <p className="fw-bold mb-0">{milestoneDetails.giftSection?.type || milestoneDetails.message || 'Regular Gift'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarFixed} alt="Set Occasion" width="32" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Set Occasion</label>
                      <p className="fw-bold mb-0">{milestoneDetails.milestone || 'Milestone'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarFixed} alt="Occasion Date" width="32" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Occasion Date</label>
                      <p className="fw-bold mb-0">{formatDate(milestoneDetails.milestoneEstimateDate || milestoneDetails.orderDate)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconDeliveryVan} alt="Delivery Schedule" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Delivery Schedule</label>
                      <p className="fw-bold mb-0">
                        {milestoneDetails.recurringDetails ? 
                          `${milestoneDetails.recurringDetails.frequency} (${milestoneDetails.recurringDetails.numberOfYears} Years)` : 
                          milestoneDetails.deliverySchedule}
                      </p>
                    </div>
                  </div>
                </div>
              </div> */}



              {/* <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarOutlook} alt="Recurring End Date" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Recurring End Date</label>
                      <p className="fw-bold mb-0">{formatDate(milestoneDetails.milestoneEstimateDate || milestoneDetails.date)}</p>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconComments} alt="Additional Comments" width="32" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Additional Comments</label>
                      <p className="fw-bold mb-0">{milestoneDetails.additionalComments || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {/* Recipient & Delivery Information */}
            <hr />

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="section-title mb-0 fw-bold" style={{ fontSize: '20px' }}>Recipient & Delivery Information</h2>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={Profile} alt="Name" width="32" className="gift-icon" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Name</label>
                        <p className="fw-bold mb-0">{milestoneDetails.recipientSnapshot?.name || milestoneDetails.userName}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={Email} alt="Email" width="32" className="gift-icon" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Email</label>
                        <p className="fw-bold mb-0">{milestoneDetails.recipientSnapshot?.email || milestoneDetails.userSnapshot?.email}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={Call} alt="Phone" width="32" className="gift-icon" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Phone</label>
                        <p className="fw-bold mb-0">{formatPhoneNumber(milestoneDetails.recipientSnapshot?.phoneNumber || milestoneDetails.userSnapshot?.phoneNumber)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={Call} alt="Alternate Phone" width="32" className="gift-icon" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Alternate Phone</label>
                        <p className="fw-bold mb-0">{milestoneDetails.recipientSnapshot?.alternatePhoneNumber || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-8">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={LocationIcon} alt="Address" width="32" className="gift-icon" />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Address</label>
                        <p className="fw-bold mb-0">
                          {(milestoneDetails.recipientSnapshot?.address1 || milestoneDetails.userSnapshot?.address1)}<br />
                          {(milestoneDetails.recipientSnapshot?.city || milestoneDetails.userSnapshot?.city)}, {' '}
                          {(milestoneDetails.recipientSnapshot?.state || milestoneDetails.userSnapshot?.state)} {' '}
                          {(milestoneDetails.recipientSnapshot?.zip || milestoneDetails.userSnapshot?.zip)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {/* Product & Payment Information Combined */}
            {milestoneDetails.paymentDetails?.amountPaid !== 0 && (
              <>
                <hr />

                <div className="row">
                  {/* Product Information */}
                  <div className="col-md-5" >
                <div className="mt-0">
                  <div className="" style={{ backgroundColor: '#EFF4FF', padding: '20px', borderRadius: '8px' }}>
                    <div className="product-payment-display">
                      <div className="row">
                        {/* Product Image and Details */}
                        <div className="col-md-4">
                          <div className="product-image-container">
                            {giftDetails && (
                              <img
                                src={giftDetails.image}
                                alt="Gift"
                                className="img-fluid rounded product-image"
                                onError={(e) => {
                                  e.currentTarget.src = ProductImg;
                                }}
                              />
                            )}
                          </div>
                        </div>

                        <div className="col-md-8">
                          <div className="product-details-section">
                            <h3 className="product-title" style={{ color: '#382F2D' }}>{giftDetails?.title || 'Gift Item'}</h3>
                            <h2 className="product-price mb-2" style={{ color: '#3957A0' }}>
                              {giftDetails?.amount ? formatCurrency(giftDetails.amount) : '$0.00'}
                            </h2>
                            <p className="product-description text-muted">
                              {giftDetails?.description || 'A thoughtful gift for a special occasion.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="col-md-7">
                <div className='row'>
                  <h3 className="section-title mb-4 fw-bold" style={{ fontSize: '20px' }}>Payment Information</h3>
                  <div className='col-md-6'>
                    <div className="info-item d-flex align-items-center mb-3">
                      <img src={IconTransaction} alt="" className="me-3" width="24" height="24" />
                      <div>
                        <label className="form-label mb-1 text-muted">Transaction ID</label>
                        <p className="fw-bold">{milestoneDetails.paymentDetails?.transactionId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <div className="info-item d-flex align-items-center mb-3">
                      <img src={IconCalendarOutlook} alt="" className="me-3" width="24" height="24" />
                      <div>
                        <label className="form-label mb-1 text-muted">Date & Time</label>
                        <p className="fw-bold">{formatDateTime(milestoneDetails.orderDate)}</p>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <div className="info-item d-flex align-items-center mb-3">
                      <img src={IconCard} alt="" className="me-3" width="24" height="24" />
                      <div>
                        <label className="form-label mb-1 text-muted">Payment Method</label>
                        <p className="fw-bold">{milestoneDetails.paymentDetails?.cardBrand ? `${milestoneDetails.paymentDetails.cardBrand.toUpperCase()} **** ${milestoneDetails.paymentDetails.cardLast4}` : 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <div className="info-item d-flex align-items-center mb-3">
                      <img src={IconCurrency} alt="" className="me-3" width="24" height="24" />
                      <div>
                        <label className="form-label mb-1 text-muted">Paid Amount</label>
                        <p className="fw-bold text-success fs-5">
                          {milestoneDetails.paymentDetails?.amountPaid ? formatCurrency(milestoneDetails.paymentDetails.amountPaid) : '$0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
              </>
            )}

            {/* Milestone Confirmation Section */}
            <hr />
            <div className="" style={{backgroundColor:'#EFF4FF', padding:'20px', borderRadius:'8px', marginTop:'20px', border:'1px solid #CFD9E3'}}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="section-title mb-0 fw-bold" style={{fontSize:'16px'}}>Milestone Confirmation</h3>
              </div>

              <div className="row align-items-end">
                <div>
                  <FormGroup className='form-group mb-0'>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="milestoneConfirmation"
                          id="milestoneYes"
                          value="Yes"
                          checked={milestoneConfirmation === 'Yes'}
                          onChange={(e) => setMilestoneConfirmation(e.target.value)}
                          disabled={updating}
                        />
                        <label className="form-check-label fw-bold mt-1" htmlFor="milestoneYes">
                          Yes
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="milestoneConfirmation"
                          id="milestoneNo"
                          value="No"
                          checked={milestoneConfirmation === 'No'}
                          onChange={(e) => setMilestoneConfirmation(e.target.value)}
                          disabled={updating}
                        />
                        <label className="form-check-label fw-bold mt-1" htmlFor="milestoneNo">
                          No
                        </label>
                      </div>
                      <Button
                        color="primary"
                        className="btn-primary"
                        disabled={updating}
                        onClick={handleMilestoneConfirmationUpdate}
                      >
                        {updating ? 'Updating...' : 'Update'}
                      </Button>
                    </div>
                  </FormGroup>
                </div>
              </div>
            </div>

          </div>









        </div>

      </section>
    </>
  );
};

export default MilestoneDetails;